import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SessionControls } from "./SessionControls";

interface Props {
  previewUrl?: string;
  mediaStream: MediaStream | null;
  isMuted: boolean;
  onToggleMute: () => void;
  isPaused?: boolean;
  onTogglePause?: () => void;
  onEnd?: () => void;
  errorMessage?: string | null;
  /**
   * "contain" (default) → fill the container, crop overflow, centered. Use for
   * full-screen / side-by-side surfaces with landscape streams.
   * "cover" → fill the container, crop overflow, anchor to top. Use for
   * the corner bubble where the container shape is fixed.
   * "native" → size the visible tile to the stream's native aspect ratio over a
   * transparent surface. No crop, no gray bars — the tile simply *is* portrait.
   * "scene-fill" → same aspect-matched tile, but the scene still (`previewUrl`)
   * fills the panel behind it and the tile's edges are feathered into it, so a
   * square stream of the same room reads as one continuous wide scene.
   */
  fit?: "contain" | "cover" | "native" | "scene-fill";
  /**
   * Compact controls for tiny surfaces (e.g. the corner bubble layout).
   * Shrinks buttons, gaps and the mic hint so they fit a ~200px container.
   */
  compact?: boolean;
  /**
   * Hide the built-in mic/pause/end control row. Use when the session
   * surface renders the controls separately (e.g. the document-with-bubble
   * layout, where controls live at the parent's center-bottom).
   */
  hideControls?: boolean;
  /** Optional camera toggle passed through to SessionControls. */
  isCameraOn?: boolean;
  onToggleCamera?: () => void;
  /** Optional screen-share toggle passed through to SessionControls. */
  isScreenSharing?: boolean;
  onToggleScreenShare?: () => void;
  /** Reports the stream's intrinsic aspect ratio once known (diagnostics). */
  onNativeRatio?: (ratio: number) => void;
  /** Fires once when the current stream paints its first playable frame. */
  onFirstFrame?: () => void;
  /**
   * Never paint the scenario thumbnail as a fallback still. Use when the panel
   * only ever shows a live stream and a poster frame would flash on
   * connect/teardown.
   */
  hidePoster?: boolean;
}

export function AvatarVideoArea({
  previewUrl, mediaStream, isMuted, onToggleMute,
  isPaused = false, onTogglePause, onEnd, errorMessage, fit = "contain", compact = false,
  hideControls = false,
  isCameraOn = false, onToggleCamera,
  isScreenSharing = false, onToggleScreenShare,
  onNativeRatio,
  onFirstFrame,
  hidePoster = false,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const [nativeRatio, setNativeRatio] = useState<number | null>(null);
  const [panelRatio, setPanelRatio] = useState<number | null>(null);
  const [needsUnmute, setNeedsUnmute] = useState(false);
  const [hasFirstFrame, setHasFirstFrame] = useState(false);
  const [unmuteReason, setUnmuteReason] = useState<
    "autoplay-blocked" | "iframe-blocked" | "unknown" | null
  >(null);
  const lateAudioRetriedRef = useRef(false);
  const onFirstFrameRef = useRef(onFirstFrame);
  onFirstFrameRef.current = onFirstFrame;
  // Bubble (cover) fills the fixed 3:4 shape and anchors to the top so the
  // face stays in view. Contain letterboxes instead of cropping, so streams
  // whose native aspect ratio differs from the panel keep their framing.
  const mediaCls =
    fit === "cover"
      ? "h-full w-full object-cover object-top"
      : fit === "native" || fit === "scene-fill"
        ? "h-full w-full object-contain object-center"
        : "h-full w-full object-cover object-center";
  const isSceneFill = fit === "scene-fill";
  // Scene-fill only makes sense while the stream is clearly narrower than the
  // panel and we actually have the scene still to fill with; otherwise it
  // degrades to the plain aspect-matched tile.
  const sceneBackdropOn =
    isSceneFill && !!previewUrl && !!nativeRatio && !!panelRatio && panelRatio - nativeRatio > 0.15;
  const isNative = fit === "native" || isSceneFill;
  const showPoster = !!previewUrl && !sceneBackdropOn && !hidePoster;

  // Attempt to play the element with or without sound.
  // Returns the resulting state so callers can react to browser-forced mute.
  const attemptPlay = async (
    el: HTMLVideoElement,
    withSound: boolean,
  ): Promise<{ playing: boolean; muted: boolean; errorName?: string }> => {
    el.muted = !withSound;
    try {
      await el.play();
      return { playing: !el.paused, muted: el.muted };
    } catch (err) {
      return {
        playing: false,
        muted: el.muted,
        errorName: (err as { name?: string })?.name ?? "unknown",
      };
    }
  };

  const classifyBlock = (errorName?: string): "iframe-blocked" | "autoplay-blocked" | "unknown" => {
    const inIframe = typeof window !== "undefined" && window.self !== window.top;
    if (errorName === "NotAllowedError") return inIframe ? "iframe-blocked" : "autoplay-blocked";
    return "unknown";
  };

  const tryUnmute = async () => {
    const el = videoRef.current;
    if (!el) return;
    const res = await attemptPlay(el, true);
    if (res.playing && !res.muted) {
      setNeedsUnmute(false);
      setUnmuteReason(null);
      return;
    }
    // Keep the pill visible; keep picture running muted so the user sees video.
    await attemptPlay(el, false);
    setUnmuteReason(classifyBlock(res.errorName));
    setNeedsUnmute(true);
  };

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !mediaStream) {
      setNeedsUnmute(false);
      setUnmuteReason(null);
      lateAudioRetriedRef.current = false;
      return;
    }
    let cancelled = false;
    lateAudioRetriedRef.current = false;
    el.srcObject = mediaStream;

    const run = async () => {
      const withSound = await attemptPlay(el, true);
      if (cancelled) return;
      if (withSound.playing && !withSound.muted) {
        setNeedsUnmute(false);
        setUnmuteReason(null);
        return;
      }
      // Fall back to muted playback so the picture is always visible.
      const muted = await attemptPlay(el, false);
      if (cancelled) return;
      if (muted.playing || withSound.errorName === "NotAllowedError") {
        setUnmuteReason(classifyBlock(withSound.errorName));
        setNeedsUnmute(true);
      }
    };
    void run();

    // If the audio track arrives after the video track, retry unmute once.
    const onAddTrack = (ev: MediaStreamTrackEvent) => {
      if (ev.track.kind !== "audio") return;
      if (lateAudioRetriedRef.current) return;
      if (!el.muted) return;
      lateAudioRetriedRef.current = true;
      void (async () => {
        const res = await attemptPlay(el, true);
        if (cancelled) return;
        if (res.playing && !res.muted) {
          setNeedsUnmute(false);
          setUnmuteReason(null);
        } else {
          await attemptPlay(el, false);
          if (cancelled) return;
          setUnmuteReason(classifyBlock(res.errorName));
          setNeedsUnmute(true);
        }
      })();
    };
    mediaStream.addEventListener("addtrack", onAddTrack);

    return () => {
      cancelled = true;
      mediaStream.removeEventListener("addtrack", onAddTrack);
    };
  }, [mediaStream]);

  // Reveal the video element only once it has actually painted a frame, so no
  // empty/black or poster frame flashes while the stream comes up.
  useEffect(() => {
    setHasFirstFrame(false);
    const el = videoRef.current;
    if (!el || !mediaStream) return;
    let cancelled = false;
    const mark = () => {
      if (!cancelled) {
        setHasFirstFrame(true);
        onFirstFrameRef.current?.();
      }
    };
    type WithRvfc = HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    };
    const rvfc = (el as WithRvfc).requestVideoFrameCallback;
    if (typeof rvfc === "function") rvfc.call(el, mark);
    el.addEventListener("playing", mark);
    el.addEventListener("loadeddata", mark);
    return () => {
      cancelled = true;
      el.removeEventListener("playing", mark);
      el.removeEventListener("loadeddata", mark);
    };
  }, [mediaStream]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !mediaStream) return;
    if (isPaused) el.pause(); else el.play().catch(() => {});
  }, [isPaused, mediaStream]);

  // Track the stream's intrinsic ratio so `native` can shape the tile and the
  // backdrop can be skipped when the stream already matches the panel.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const read = () => {
      if (el.videoWidth && el.videoHeight) {
        const ratio = el.videoWidth / el.videoHeight;
        setNativeRatio(ratio);
        onNativeRatio?.(ratio);
      }
    };
    read();
    el.addEventListener("loadedmetadata", read);
    el.addEventListener("resize", read);
    return () => {
      el.removeEventListener("loadedmetadata", read);
      el.removeEventListener("resize", read);
    };
  }, [mediaStream]);

  // Panel ratio decides which axis the native tile fills.
  useEffect(() => {
    if (!isNative) return;
    const el = rootRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setPanelRatio(r.width / r.height);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isNative]);

  const pillTooltip =
    unmuteReason === "iframe-blocked"
      ? "Your LMS is blocking autoplay. If sound doesn't start after tapping, open this activity in a new tab."
      : unmuteReason === "autoplay-blocked"
        ? "Your browser blocked sound until you interact with the page. Tap to enable."
        : undefined;

  const media = (
    <>
      {showPoster ? (
        <img src={previewUrl} alt="" className={cn("absolute inset-0", mediaCls)} />
      ) : null}
      {mediaStream && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className={cn(
            "absolute inset-0 transition-opacity duration-150",
            mediaCls,
            hasFirstFrame ? "opacity-100" : "opacity-0",
          )}
        />
      )}
    </>
  );

  // Feather the tile's vertical edges so the live square blends into the
  // still behind it instead of reading as a pasted-on rectangle.
  const featherStyle: CSSProperties = sceneBackdropOn
    ? {
        maskImage:
          "linear-gradient(to right, transparent 0%, black 9%, black 91%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(to right, transparent 0%, black 9%, black 91%, transparent 100%)",
      }
    : {};

  const nativeStyle: CSSProperties = nativeRatio
    ? panelRatio && nativeRatio > panelRatio
      ? { aspectRatio: String(nativeRatio), width: "100%", height: "auto" }
      : { aspectRatio: String(nativeRatio), height: "100%", width: "auto" }
    : { height: "100%", width: "100%" };

  return (
    <div
      ref={rootRef}
      className={cn(
        "relative h-full w-full flex items-center justify-center overflow-hidden",
        isNative ? "bg-transparent" : "bg-accent",
      )}
    >
      {sceneBackdropOn && (
        // The same scene as the stream, widened to fill the panel.
        <img
          src={previewUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
      )}
      {isNative ? (
        // Aspect-matched tile: one axis fills the panel, the other follows the
        // stream's native ratio, so nothing is cropped and nothing is padded
        // with a visible band (the surface behind it is transparent).
        <div className="relative shrink-0" style={{ ...nativeStyle, ...featherStyle }}>
          {media}
        </div>
      ) : (
        media
      )}
      {errorMessage && (
        <div className="absolute bottom-0 left-0 right-0 bg-destructive px-4 py-2 text-center">
          <p className="text-xs text-destructive-foreground">{errorMessage}</p>
        </div>
      )}
      {!hideControls && (
        <SessionControls
          isMuted={isMuted}
          onToggleMute={onToggleMute}
          isPaused={isPaused}
          onTogglePause={onTogglePause}
          onEnd={onEnd}
          isCameraOn={isCameraOn}
          onToggleCamera={onToggleCamera}
          isScreenSharing={isScreenSharing}
          onToggleScreenShare={onToggleScreenShare}
          compact={compact}
          className={cn(
            "absolute left-1/2 -translate-x-1/2",
            compact ? "bottom-3" : "bottom-6",
          )}
        />
      )}
      {needsUnmute && (
        <Button
          onClick={tryUnmute}
          size="sm"
          className="absolute top-3 right-3 rounded-full shadow-lg"
          title={pillTooltip}
          aria-live="polite"
        >
          <Volume2 className="h-4 w-4" />Tap for sound
        </Button>
      )}
    </div>
  );
}
